/**
 * Checkout Analytics v2.0
 *
 * Сквозной идентификатор: batch_ordering_id (генерируется при begin_checkout).
 * Принцип Source & Trigger: source — откуда данные, trigger — как отправлено.
 */

// ─── Slug-маппинг доставки ───────────────────────────────────────────────────
const DELIVERY_SLUG_MAP = {
  PICK_POINT: 'pickup',
  COURIER: 'courier',
  POST: 'post',
}

export function getDeliverySlug(deliveryTypeId) {
  if (!deliveryTypeId) return null
  return DELIVERY_SLUG_MAP[deliveryTypeId] || deliveryTypeId.toLowerCase()
}

export function getPaymentSlug(paymentName) {
  return paymentName || null
}

// ─── batch_ordering_id ──────────────────────────────────────────────────────
// Сохраняем в sessionStorage, чтобы id переживал F5 в той же вкладке (TC-24).
const BATCH_ORDERING_ID_KEY = 'checkout_batch_ordering_id'
let _batchOrderingId = null

function readSavedBatchOrderingId() {
  try {
    return sessionStorage.getItem(BATCH_ORDERING_ID_KEY)
  } catch (e) {
    return null
  }
}

function writeSavedBatchOrderingId(id) {
  try {
    sessionStorage.setItem(BATCH_ORDERING_ID_KEY, id)
  } catch (e) {
    // sessionStorage недоступен — продолжаем работу с in-memory id
  }
}

export function generateBatchOrderingId() {
  _batchOrderingId =
    'co_' +
    Date.now().toString(36) +
    '_' +
    Math.random().toString(36).slice(2, 8)
  writeSavedBatchOrderingId(_batchOrderingId)
  return _batchOrderingId
}

export function getBatchOrderingId() {
  if (_batchOrderingId) return _batchOrderingId
  const saved = readSavedBatchOrderingId()
  if (saved) {
    _batchOrderingId = saved
    return _batchOrderingId
  }
  return generateBatchOrderingId()
}

const APP_VERSION_FALLBACK = '2.0'
let _appVersion = null

export function getAppVersion() {
  if (_appVersion !== null) return _appVersion
  let version = new URLSearchParams(window.location.search).get('version')
  _appVersion = version || APP_VERSION_FALLBACK
  return _appVersion
}

// ─── user_status ─────────────────────────────────────────────────────────────
export function getUserStatus() {
  const dataLayer = window.dataLayer || []
  const entry = dataLayer.find((item) => item.event === 'userId')
  return entry?.user_status || null
}

// Глобальные поля (batch_ordering_id, user_status) присутствуют в каждом
// событии. Остальные ключи задаёт только сам трекер — событие не должно слать
// неотносящиеся к нему параметры в виде null (ТЗ v2.0, §2 и §4).
function pushEvent(event, checkoutData) {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event,
    checkout_data: {
      batch_ordering_id: getBatchOrderingId(),
      user_status: getUserStatus(),
      app_version: getAppVersion(),
      ...checkoutData,
    },
  })
}

function pushEcomEvent(data) {
  window.dataLayer = window.dataLayer || []
  data.ecommerce = data.ecommerce || {}
  data.ecommerce.items = data.ecommerce.items || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push(data)
}

export function toGaItems(basketItems) {
  return Array.isArray(basketItems)
    ? basketItems.map((i) => ({
        item_name: i.name,
        item_brand: i.brand,
        item_category: i.category,
        item_variant: i.variant,
        item_id: i.article,
        price: i.price,
        quantity: i.quantity,
      }))
    : []
}

export function trackPurchase(data) {
  pushEcomEvent({
    ...data,
    checkout_data: {
      batch_ordering_id: getBatchOrderingId(),
      user_status: getUserStatus(),
      app_version: getAppVersion(),
    },
  })
}

export function trackStepViewed({
  step,
  totalValue,
  deliveryMethod,
  paymentMethod,
}) {
  pushEvent('checkout_step_viewed', {
    step,
    total_value: totalValue != null ? parseFloat(totalValue) : null,
    delivery_method: getDeliverySlug(deliveryMethod) || null,
    payment_method: getPaymentSlug(paymentMethod) || null,
  })
}

// ─── Дедуп выбора метода ─────────────────────────────────────────────────────
// Доставку дедуплицируем только для auto_prefill — каждое
// действие пользователя даёт своё событие. Сброс при смене профиля.
let prefilledDeliveryMethods = {}
let lastReportedPaymentMethod = null

export function resetMethodSelectionDedup() {
  prefilledDeliveryMethods = {}
  lastReportedPaymentMethod = null
}

export function trackDeliveryMethodSelected({ source, trigger, method }) {
  const slug = getDeliverySlug(method)
  if (trigger === 'auto_prefill' && slug) {
    if (prefilledDeliveryMethods[slug]) return
    prefilledDeliveryMethods[slug] = true
  }
  pushEvent('delivery_method_selected', {
    source: slug === 'pickup' ? 'prefill' : source,
    trigger,
    method: slug,
  })
}

export function trackPaymentMethodSelected({ source, trigger, method }) {
  const slug = getPaymentSlug(method)
  if (slug && slug === lastReportedPaymentMethod) return
  lastReportedPaymentMethod = slug
  pushEvent('payment_method_selected', {
    source,
    trigger,
    method: slug,
  })
}

export function trackProfileAction({ action, currentStep }) {
  pushEvent('checkout_profile_action', {
    action,
    current_step: currentStep,
  })
}

export function trackCheckoutError({
  step,
  errorType,
  errorMessage,
  sentryEventId,
}) {
  pushEvent('checkout_error', {
    step: step || null,
    error_type: errorType,
    error_message: errorMessage || null,
    sentry_event_id: sentryEventId || null,
  })
}
